/**
 * Cycle Configuration Session Store
 * 
 * Manages the state and operations for cycle configuration sessions.
 * These sessions handle pre-prioritization decisions about criteria,
 * weights, tools, value providers, and participants.
 */

import { create } from 'zustand';
// Removed persist - Supabase is now the primary data store
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync';
import {
  type CycleConfigurationSession,
  type CycleConfigState,
  type CycleConfigScope,
  type CycleConfigDecisionType,
  type CycleConfigDecisionStatus,
  type CycleConfigParticipant,
  type ConfigParticipantRole,
  type CriteriaDecisionValue,
  type WeightsDecisionValue,
  type ToolsDecisionValue,
  type ValueProvidersDecisionValue,
  type ParticipantsDecisionValue,
  type AttributeRangesDecisionValue,
  type AttributeRangeConfig,
  type DecisionHistoryEntry,
  type ValueProviderConfig,
  createEmptyConfigSession,
  getDecision,
  canLockSession,
} from '../../types/prioritization/cycleConfigSession';
import type { SessionTimingMode, SessionEntryMode } from '../../types/prioritization/session';
import type { ActorRole } from '../../types/prioritization/roles';

// ============================================================================
// Store State Interface
// ============================================================================

interface CycleConfigStoreState {
  /** All configuration sessions indexed by ID */
  sessions: Map<string, CycleConfigurationSession>;
  
  /** Active configuration session ID per cycle */
  activeSessions: Map<string, string>; // cycleId -> configSessionId
  
  /** Loading states */
  loading: boolean;
  error: string | null;
}

interface CycleConfigActions {
  // CRUD Operations
  createConfigSession: (
    cycleId: string,
    name: string,
    createdBy: string,
    options?: {
      scope?: Partial<CycleConfigScope>;
      timingMode?: SessionTimingMode;
      entryMode?: SessionEntryMode;
    }
  ) => CycleConfigurationSession;
  
  getConfigSession: (sessionId: string) => CycleConfigurationSession | undefined;
  
  getActiveConfigSession: (cycleId: string) => CycleConfigurationSession | undefined;
  
  getConfigSessionsForCycle: (cycleId: string) => CycleConfigurationSession[];
  
  deleteConfigSession: (sessionId: string) => void;
  
  // State Management
  updateSessionState: (sessionId: string, state: CycleConfigState) => void;
  
  startConfigSession: (sessionId: string) => void;
  
  lockConfigSession: (sessionId: string, userId: string, reason?: string) => {
    success: boolean;
    errors: string[];
  };
  
  supersedeConfigSession: (sessionId: string, newSessionId: string) => void;
  
  // Participant Management
  addParticipant: (
    sessionId: string,
    userId: string,
    role: ConfigParticipantRole,
    addedBy: string,
    appRoles?: ActorRole[]
  ) => void;
  
  removeParticipant: (sessionId: string, userId: string) => void;
  
  updateParticipantRole: (sessionId: string, userId: string, role: ConfigParticipantRole) => void;
  
  recordParticipation: (sessionId: string, userId: string) => void;
  
  // Decision Management - Generic
  getDecisionValue: <T>(sessionId: string, type: CycleConfigDecisionType) => T | undefined;
  
  proposeDecision: (
    sessionId: string,
    type: CycleConfigDecisionType,
    value: unknown,
    userId: string,
    rationale?: string
  ) => void;
  
  lockDecision: (
    sessionId: string,
    type: CycleConfigDecisionType,
    userId: string,
    rationale?: string
  ) => void;
  
  unlockDecision: (
    sessionId: string,
    type: CycleConfigDecisionType,
    userId: string,
    rationale?: string
  ) => void;
  
  // Decision Management - Typed Helpers
  proposeCriteria: (sessionId: string, criteriaIds: string[], userId: string) => void;
  
  proposeWeights: (
    sessionId: string,
    weights: Record<string, number>,
    method: 'equal' | 'manual' | 'pairwise',
    userId: string
  ) => void;
  
  proposeTools: (sessionId: string, toolIds: string[], primaryToolId: string | undefined, userId: string) => void;
  
  proposeValueProviders: (sessionId: string, configs: ValueProviderConfig[], userId: string) => void;
  
  proposeAttributeRanges: (sessionId: string, ranges: AttributeRangeConfig[], userId: string) => void;
  
  proposeParticipants: (
    sessionId: string,
    participants: ParticipantsDecisionValue['sessionParticipants'],
    userId: string
  ) => void;
  
  // Utility
  setActiveConfigSession: (cycleId: string, sessionId: string) => void;
  
  clearError: () => void;
}

type CycleConfigStore = CycleConfigStoreState & CycleConfigActions & {
  loadSessionsFromSupabase: () => Promise<void>;
};

// ============================================================================
// Supabase Sync Helpers
// ============================================================================

const TABLE_NAME = 'cycle_config_sessions';

interface ConfigSessionDbRecord {
  id: string;
  workspace_id: string | null;
  cycle_id: string;
  name: string;
  state: string;
  session_data: unknown;
  created_by: string;
  created_at: string;
  updated_at: string;
}

function sessionToDbFormat(session: CycleConfigurationSession): ConfigSessionDbRecord {
  return {
    id: session.id,
    workspace_id: getCurrentWorkspaceId(),
    cycle_id: session.cycleId,
    name: session.name,
    state: session.state,
    session_data: session,
    created_by: session.createdBy,
    created_at: session.createdAt.toISOString(),
    updated_at: session.updatedAt.toISOString(),
  };
}

function dbToSession(row: ConfigSessionDbRecord): CycleConfigurationSession {
  // The full session is stored in session_data
  const data = row.session_data as CycleConfigurationSession;
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    lockedAt: data.lockedAt ? new Date(data.lockedAt) : undefined,
  };
}

async function insertSessionToSupabase(session: CycleConfigurationSession): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  try {
    const { error } = await supabase.from(TABLE_NAME).insert(sessionToDbFormat(session));
    if (error) {
      console.error('[CycleConfigStore] Supabase insert error:', error);
    } else {
      console.log('[CycleConfigStore] Session synced to Supabase:', session.id);
    }
  } catch (err) {
    console.error('[CycleConfigStore] Supabase insert exception:', err);
  }
}

async function updateSessionInSupabase(session: CycleConfigurationSession): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  try {
    const { error } = await supabase.from(TABLE_NAME).update(sessionToDbFormat(session)).eq('id', session.id);
    if (error) {
      console.error('[CycleConfigStore] Supabase update error:', error);
    }
  } catch (err) {
    console.error('[CycleConfigStore] Supabase update exception:', err);
  }
}

/**
 * The configure wizard's draft is the one thing this table holds; a draft change
 * that stayed in memory was lost on reload (§2.2). Every change below saves it.
 */
function persistSession(session: CycleConfigurationSession | undefined): void {
  if (session) void updateSessionInSupabase(session);
}

async function deleteSessionFromSupabase(sessionId: string): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  try {
    const { error } = await supabase.from(TABLE_NAME).delete().eq('id', sessionId);
    if (error) {
      console.error('[CycleConfigStore] Supabase delete error:', error);
    }
  } catch (err) {
    console.error('[CycleConfigStore] Supabase delete exception:', err);
  }
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useCycleConfigStore = create<CycleConfigStore>()((set, get) => ({
      // Initial State
      sessions: new Map(),
      activeSessions: new Map(),
      loading: false,
      error: null,
      
      // ================================
      // Supabase Sync
      // ================================
      
      loadSessionsFromSupabase: async () => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          console.log('[CycleConfigStore] Supabase not available');
          return;
        }
        
        set({ loading: true, error: null });
        // A read can land after the configure page has made a draft (the Layout
        // loads these on start, and a direct open creates one at once), so a draft
        // made after this read began is kept rather than wiped by its answer.
        const readStartedAt = Date.now();
        
        try {
          let query = supabase.from(TABLE_NAME).select('*');
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
          }
          
          const { data, error } = await query.order('created_at', { ascending: false });
          
          if (error) {
            console.error('[CycleConfigStore] Supabase load error:', error);
            set({ loading: false, error: error.message });
            return;
          }
          
          const sessionsMap = new Map<string, CycleConfigurationSession>();
          const activeSessionsMap = new Map<string, string>();
          
          (data || []).forEach((row: ConfigSessionDbRecord) => {
            const session = dbToSession(row);
            sessionsMap.set(session.id, session);
            // Set as active if it's not locked
            if (session.state !== 'locked' && session.state !== 'superseded') {
              activeSessionsMap.set(session.cycleId, session.id);
            }
          });
          
          for (const [id, session] of get().sessions) {
            if (sessionsMap.has(id) || new Date(session.createdAt).getTime() < readStartedAt) continue;
            sessionsMap.set(id, session);
            if (session.state !== 'locked' && session.state !== 'superseded' && !activeSessionsMap.has(session.cycleId)) {
              activeSessionsMap.set(session.cycleId, id);
            }
          }
          
          set({ sessions: sessionsMap, activeSessions: activeSessionsMap, loading: false, error: null });
          console.log(`[CycleConfigStore] Loaded ${sessionsMap.size} sessions from Supabase`);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          console.error('[CycleConfigStore] Supabase load exception:', err);
          set({ loading: false, error: message });
        }
      },
      
      // ================================
      // CRUD Operations
      // ================================
      
      createConfigSession: (cycleId, name, createdBy, options = {}) => {
        const session = createEmptyConfigSession(cycleId, name, createdBy, options.scope);
        
        // Apply optional overrides
        if (options.timingMode) {
          session.timingMode = options.timingMode;
        }
        if (options.entryMode) {
          session.entryMode = options.entryMode;
        }
        
        set((state) => {
          const newSessions = new Map(state.sessions);
          newSessions.set(session.id, session);
          
          // Set as active session for this cycle
          const newActiveSessions = new Map(state.activeSessions);
          newActiveSessions.set(cycleId, session.id);
          
          return { 
            sessions: newSessions,
            activeSessions: newActiveSessions,
          };
        });
        
        // Sync to Supabase
        insertSessionToSupabase(session);
        
        return session;
      },
      
      getConfigSession: (sessionId) => {
        return get().sessions.get(sessionId);
      },
      
      getActiveConfigSession: (cycleId) => {
        const activeSessionId = get().activeSessions.get(cycleId);
        if (!activeSessionId) return undefined;
        return get().sessions.get(activeSessionId);
      },
      
      getConfigSessionsForCycle: (cycleId) => {
        return Array.from(get().sessions.values())
          .filter(s => s.cycleId === cycleId)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      },
      
      deleteConfigSession: (sessionId) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          const newSessions = new Map(state.sessions);
          newSessions.delete(sessionId);
          
          // Clear from active if this was active
          const newActiveSessions = new Map(state.activeSessions);
          if (newActiveSessions.get(session.cycleId) === sessionId) {
            newActiveSessions.delete(session.cycleId);
          }
          
          return { 
            sessions: newSessions,
            activeSessions: newActiveSessions,
          };
        });
        
        // Sync to Supabase
        deleteSessionFromSupabase(sessionId);
      },
      
      // ================================
      // State Management
      // ================================
      
      updateSessionState: (sessionId, newState) => {
        const session = get().sessions.get(sessionId);
        if (!session) return;
        
        const updatedSession: CycleConfigurationSession = {
          ...session,
          state: newState,
          updatedAt: new Date(),
          version: session.version + 1,
        };
        
        set((state) => {
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, updatedSession);
          
          return { sessions: newSessions };
        });
        
        // Sync to Supabase
        updateSessionInSupabase(updatedSession);
      },
      
      startConfigSession: (sessionId) => {
        get().updateSessionState(sessionId, 'in_progress');
      },
      
      lockConfigSession: (sessionId, userId, reason) => {
        const session = get().sessions.get(sessionId);
        if (!session) {
          return { success: false, errors: ['Session not found'] };
        }
        
        const { canLock, reasons } = canLockSession(session);
        if (!canLock) {
          return { success: false, errors: reasons };
        }
        
        set((state) => {
          const newSessions = new Map(state.sessions);
          const now = new Date();
          
          // Lock all pending decisions
          const lockedDecisions = session.decisions.map(d => {
            if (d.status !== 'locked') {
              return {
                ...d,
                status: 'locked' as CycleConfigDecisionStatus,
                lockedBy: userId,
                lockedAt: now,
                history: [
                  ...d.history,
                  {
                    timestamp: now,
                    userId,
                    action: 'locked' as const,
                    value: d.value,
                    rationale: reason,
                  },
                ],
              };
            }
            return d;
          });
          
          newSessions.set(sessionId, {
            ...session,
            decisions: lockedDecisions,
            state: 'locked',
            lockedAt: now,
            lockedBy: userId,
            lockReason: reason,
            updatedAt: now,
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
        persistSession(get().sessions.get(sessionId));
        
        return { success: true, errors: [] };
      },
      
      supersedeConfigSession: (sessionId, newSessionId) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, {
            ...session,
            state: 'superseded',
            updatedAt: new Date(),
            version: session.version + 1,
          });
          
          // Update active session
          const newActiveSessions = new Map(state.activeSessions);
          newActiveSessions.set(session.cycleId, newSessionId);
          
          return { 
            sessions: newSessions,
            activeSessions: newActiveSessions,
          };
        });
      },
      
      // ================================
      // Participant Management
      // ================================
      
      addParticipant: (sessionId, userId, role, addedBy, appRoles) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          // Check if already a participant
          if (session.participants.some(p => p.userId === userId)) {
            return state;
          }
          
          const newParticipant: CycleConfigParticipant = {
            userId,
            role,
            addedAt: new Date(),
            addedBy,
            appRoles,
            hasParticipated: false,
          };
          
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, {
            ...session,
            participants: [...session.participants, newParticipant],
            updatedAt: new Date(),
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
      },
      
      removeParticipant: (sessionId, userId) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, {
            ...session,
            participants: session.participants.filter(p => p.userId !== userId),
            updatedAt: new Date(),
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
      },
      
      updateParticipantRole: (sessionId, userId, role) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, {
            ...session,
            participants: session.participants.map(p =>
              p.userId === userId ? { ...p, role } : p
            ),
            updatedAt: new Date(),
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
      },
      
      recordParticipation: (sessionId, userId) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          
          const now = new Date();
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, {
            ...session,
            participants: session.participants.map(p =>
              p.userId === userId 
                ? { ...p, hasParticipated: true, lastParticipatedAt: now } 
                : p
            ),
            updatedAt: now,
          });
          
          return { sessions: newSessions };
        });
      },
      
      // ================================
      // Decision Management - Generic
      // ================================
      
      getDecisionValue: <T>(sessionId: string, type: CycleConfigDecisionType) => {
        const session = get().sessions.get(sessionId);
        if (!session) return undefined;
        
        const decision = getDecision(session, type);
        return decision?.value as T | undefined;
      },
      
      proposeDecision: (sessionId, type, value, userId, rationale) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          if (session.state === 'locked' || session.state === 'superseded') return state;
          
          const now = new Date();
          const newSessions = new Map(state.sessions);
          
          const historyEntry: DecisionHistoryEntry = {
            timestamp: now,
            userId,
            action: 'proposed',
            value,
            rationale,
          };
          
          // A draft made before a decision type existed has no entry for it: add one.
          const decisions = session.decisions.some(d => d.type === type)
            ? session.decisions
            : [...session.decisions, { type, status: 'pending' as CycleConfigDecisionStatus, value: undefined, history: [] }];

          newSessions.set(sessionId, {
            ...session,
            decisions: decisions.map(d => {
              if (d.type === type) {
                return {
                  ...d,
                  value,
                  status: 'proposed' as CycleConfigDecisionStatus,
                  history: [...d.history, historyEntry],
                };
              }
              return d;
            }),
            updatedAt: now,
            version: session.version + 1,
          });

          return { sessions: newSessions };
        });
        persistSession(get().sessions.get(sessionId));
      },
      
      lockDecision: (sessionId, type, userId, rationale) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          if (session.state === 'locked' || session.state === 'superseded') return state;
          
          const now = new Date();
          const newSessions = new Map(state.sessions);
          
          newSessions.set(sessionId, {
            ...session,
            decisions: session.decisions.map(d => {
              if (d.type === type && d.status !== 'locked') {
                return {
                  ...d,
                  status: 'locked' as CycleConfigDecisionStatus,
                  lockedBy: userId,
                  lockedAt: now,
                  history: [
                    ...d.history,
                    {
                      timestamp: now,
                      userId,
                      action: 'locked' as const,
                      value: d.value,
                      rationale,
                    },
                  ],
                };
              }
              return d;
            }),
            updatedAt: now,
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
        persistSession(get().sessions.get(sessionId));
      },
      
      unlockDecision: (sessionId, type, userId, rationale) => {
        set((state) => {
          const session = state.sessions.get(sessionId);
          if (!session) return state;
          if (session.state === 'locked' || session.state === 'superseded') return state;
          
          const now = new Date();
          const newSessions = new Map(state.sessions);
          
          newSessions.set(sessionId, {
            ...session,
            decisions: session.decisions.map(d => {
              if (d.type === type && d.status === 'locked') {
                return {
                  ...d,
                  status: 'proposed' as CycleConfigDecisionStatus,
                  lockedBy: undefined,
                  lockedAt: undefined,
                  history: [
                    ...d.history,
                    {
                      timestamp: now,
                      userId,
                      action: 'unlocked' as const,
                      value: d.value,
                      rationale,
                    },
                  ],
                };
              }
              return d;
            }),
            updatedAt: now,
            version: session.version + 1,
          });
          
          return { sessions: newSessions };
        });
        persistSession(get().sessions.get(sessionId));
      },
      
      // ================================
      // Decision Management - Typed Helpers
      // ================================
      
      proposeCriteria: (sessionId, criteriaIds, userId) => {
        const value: CriteriaDecisionValue = {
          selectedCriteriaIds: criteriaIds,
          criteriaLabels: {}, // Would be populated from criteria store
        };
        get().proposeDecision(sessionId, 'criteria', value, userId);
      },
      
      proposeWeights: (sessionId, weights, method, userId) => {
        const value: WeightsDecisionValue = {
          weights,
          method,
        };
        get().proposeDecision(sessionId, 'weights', value, userId);
      },
      
      proposeTools: (sessionId, toolIds, primaryToolId, userId) => {
        const value: ToolsDecisionValue = {
          selectedToolIds: toolIds,
          primaryToolId,
          toolConfigs: {},
        };
        get().proposeDecision(sessionId, 'tools', value, userId);
      },
      
      proposeValueProviders: (sessionId, configs, userId) => {
        const value: ValueProvidersDecisionValue = {
          attributeConfigs: configs,
        };
        get().proposeDecision(sessionId, 'value_providers', value, userId);
      },
      
      proposeParticipants: (sessionId, participants, userId) => {
        const value: ParticipantsDecisionValue = {
          sessionParticipants: participants,
        };
        get().proposeDecision(sessionId, 'participants', value, userId);
      },
      
      proposeAttributeRanges: (sessionId, ranges, userId) => {
        const allRangesAccepted = ranges.every(r => r.status === 'accepted');
        const value: AttributeRangesDecisionValue = {
          attributeRanges: ranges,
          allRangesAccepted,
        };
        get().proposeDecision(sessionId, 'attribute_ranges', value, userId);
      },
      
      // ================================
      // Utility
      // ================================
      
      setActiveConfigSession: (cycleId, sessionId) => {
        set((state) => {
          const newActiveSessions = new Map(state.activeSessions);
          newActiveSessions.set(cycleId, sessionId);
          return { activeSessions: newActiveSessions };
        });
      },
      
      clearError: () => set({ error: null }),
    }));

export default useCycleConfigStore;
